import { useState, useCallback } from 'react';
import waApi from '../../services/whatsapp/waApi';

interface Sentimento {
  positivo: number;
  neutro: number;
  negativo: number;
}

interface Topico {
  nome: string;
  quantidade: number;
}

export interface DashboardAIInsights {
  sentimentos: Sentimento;
  topicos: Topico[];
  // O backend devolve recomendacoes como strings; a UI ja trata string|objeto.
  recomendacoes: string[];
}

interface UseDashboardAIReturn {
  insights: DashboardAIInsights | null;
  isLoading: boolean;
  error: string | null;
  /** Data/hora ISO da ultima analise gerada pelo backend (null se nunca rodou) */
  generatedAt: string | null;
  // eslint-disable-next-line no-unused-vars
  fetchInsights: (metricas?: Record<string, any>, force?: boolean) => void;
}

/**
 * Hook que busca a analise de IA do dashboard via GET /dashboard/ai-analysis
 * do whatsapp-api (sentimento + topicos + recomendacoes + generated_at). E
 * usado sob demanda (botao "Executar Analise IA") — nao deve rodar no mount.
 *
 * O endpoint exige papel admin ou supervisor (403 caso contrario).
 */
export function useWaDashboardAI(): UseDashboardAIReturn {
  const [insights, setInsights] = useState<DashboardAIInsights | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  const fetchInsights = useCallback(async (metricas?: Record<string, any>, force = false) => {
    // Guard "sem_dados" so quando o caller passa metricas e nao forca: evita
    // chamar a IA com pouquissimas conversas. Sob demanda (force) sempre roda.
    const total = metricas?.total_conversas ?? null;
    if (!force && total !== null && total < 3) {
      setInsights(null);
      setError('sem_dados');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await waApi.get('/dashboard/ai-analysis', { timeout: 30000 });
      const data = res.data || {};

      // Mapeamento defensivo do shape do backend (EN/PT) para o shape interno PT.
      const s = data.sentiment_summary || {};
      const sentimentos: Sentimento = {
        positivo: Number(s.positivo ?? s.positive ?? 0),
        neutro: Number(s.neutro ?? s.neutral ?? 0),
        negativo: Number(s.negativo ?? s.negative ?? 0),
      };
      const topicos: Topico[] = (data.common_topics || []).map((t: any) => ({
        nome: t.topic ?? t.nome ?? '',
        quantidade: Number(t.count ?? t.quantidade ?? 0),
      }));
      const recomendacoes: string[] = Array.isArray(data.recommendations)
        ? data.recommendations.filter((r: any) => typeof r === 'string')
        : [];

      setInsights({ sentimentos, topicos, recomendacoes });
      setGeneratedAt(data.generated_at ?? null);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError('sem_permissao');
      } else {
        setError(err?.response?.data?.detail || 'Erro ao buscar analise de IA');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { insights, isLoading, error, generatedAt, fetchInsights };
}
